import { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import { CreditCard, Lock, ShieldCheck, CheckCircle2, ArrowLeft, Calendar, MapPin, User } from "lucide-react";
import { useStudentBooking } from "@/hooks/useStudentBooking";

export default function Checkout() {
  const { classId } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const selectedScheduleId = Number(
    classId ??
    location.state?.selectedCourse?.CourseScheduleId ??
    location.state?.selectedCourse?.courseScheduleId ??
    location.state?.selectedCourse?.id ??
    0
  );
  const [cls, setCourse] = useState(null);
  const [courseLoading, setCourseLoading] = useState(selectedScheduleId > 0);
  const [courseError, setCourseError] = useState(
    selectedScheduleId > 0 ? "" : "Please select a class before opening checkout."
  );
  const courseScheduleId = selectedScheduleId;

  const {
    step,
    setStep,
    student,
    setStudent,
    processing,
    bookingResponse,
    subtotal,
    total,
    scheduleDate,
    scheduleTime,
    locationText,
    trainerName,
    createPaymentIFrame,
    submitBooking,
  } = useStudentBooking(cls, courseScheduleId);

  const iframeRef = useRef(null);
  const paymentFrameRef = useRef(null);
  const [paymentError, setPaymentError] = useState("");
  const [iframeLoading, setIframeLoading] = useState(false);
  const [payAdvantageAssetBase, setPayAdvantageAssetBase] = useState("");
  const [paymentScriptReady, setPaymentScriptReady] = useState(false);

  useEffect(() => {
    if (selectedScheduleId <= 0) return;

    const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
    setCourseLoading(true);
    setCourseError("");
    fetch(`${apiBaseUrl}/Course/GetUpcomingCourse?pCourseScheduleId=${encodeURIComponent(selectedScheduleId)}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || "Unable to load the selected class.");
        return data;
      })
      .then(setCourse)
      .catch((error) => setCourseError(error.message))
      .finally(() => setCourseLoading(false));
  }, [selectedScheduleId]);

  useEffect(() => {
    const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
    fetch(`${apiBaseUrl}/PayAdvantage/config`)
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load payment configuration.");
        return response.json();
      })
      .then((config) => {
        if (!config?.assetBaseUrl) throw new Error("Payment asset URL is not configured.");
        setPayAdvantageAssetBase(config.assetBaseUrl.replace(/\/$/, ""));
      })
      .catch((error) => setPaymentError(error.message));
  }, []);

  useEffect(() => {
    if (!payAdvantageAssetBase) return;

    let link = document.getElementById("payadvantage-payment-css");
    if (!link) {
      link = document.createElement("link");
      link.id = "payadvantage-payment-css";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = `${payAdvantageAssetBase}/paymentiframe.css`;

    const expectedScriptUrl = `${payAdvantageAssetBase}/paymentiframe.js`;
    let script = document.querySelector('script[data-payadvantage-payment="true"]');
    if (!script || script.src !== expectedScriptUrl) {
      script?.remove();
      script = document.createElement("script");
      script.src = expectedScriptUrl;
      script.async = true;
      script.dataset.payadvantagePayment = "true";
      script.onload = () => setPaymentScriptReady(true);
      script.onerror = () => setPaymentError("Unable to load the Pay Advantage payment form.");
      document.body.appendChild(script);
    } else if (window.PayAdvantagePaymentIFrame) {
      setPaymentScriptReady(true);
    }
  }, [payAdvantageAssetBase]);

  useEffect(() => () => paymentFrameRef.current?.dispose?.(), []);

  const pay = async (e) => {
    e.preventDefault();
    setPaymentError("");
    setIframeLoading(true);
    paymentFrameRef.current?.dispose?.();

    try {
      const result = await createPaymentIFrame(courseScheduleId, student);
      if (!result?.iframeUrl) throw new Error("No payment iframe URL was returned.");
      if (!window.PayAdvantagePaymentIFrame) {
        throw new Error("The Pay Advantage payment form is still loading. Please try again.");
      }

      const paymentFrame = new window.PayAdvantagePaymentIFrame();
      paymentFrameRef.current = paymentFrame;
      paymentFrame.addEventListener("complete", async (event) => {
        const detail = event?.detail || {};
        if (detail.status === "success") {
          const reference = detail.code || detail.payment?.code;
          if (!reference) {
            setPaymentError("Payment completed, but no payment reference was returned.");
            return;
          }
          await submitBooking(
            student,
            courseScheduleId,
            reference,
            { ...detail, customerCode: result.customerCode }
          );
        } else if (detail.status === "undetermined") {
          setPaymentError("Payment is still being confirmed. Please do not try another payment yet.");
        } else if (detail.status === "failed") {
          setPaymentError(detail.failReason || "Payment was declined. Please try another card.");
        }
      });
      await paymentFrame.initialise(
        iframeRef.current,
        result.iframeUrl,
        { showCard: true, showSummary: true },
        { firstName: student.firstName, lastName: student.lastName, email: student.email }
      );
    } catch (error) {
      const apiError = error?.response?.data;

      let validationErrors = "";

      if (Array.isArray(apiError?.errors)) {
        validationErrors = apiError.errors.map(x => x.message).filter(Boolean).join(" ");
      } else if (apiError?.errors) {
        validationErrors = Object.values(apiError.errors).flat().join(" ");
      }

      setPaymentError(
        apiError?.message ||
        validationErrors ||
        error?.message ||
        "Unable to initialise payment."
      );
    } finally {
      setIframeLoading(false);
    }
  };

  if (courseLoading) {
    return <div className="max-w-2xl mx-auto px-4 py-16 text-center text-slate-600">Loading class details...</div>;
  }

  if (courseError || !cls) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="wc-card p-8 text-center">
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">Class not selected</h1>
          <p className="text-slate-600 mt-2">{courseError || "Unable to load the selected class."}</p>
          <Link to="/calendar" className="wc-btn-primary text-sm mt-6">Choose a class</Link>
        </div>
      </div>
    );
  }

  if (step === "done") {
    const summary = bookingResponse?.bookingSummary || {};
    const isSuccess = bookingResponse?.success === true && bookingResponse?.statusCode === 1;
    
    if (!isSuccess) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="wc-card p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8" /></div>
            <h1 className="text-3xl font-display font-extrabold text-[#0A2540]">Booking failed</h1>
            <p className="text-slate-600 mt-2">{bookingResponse?.message || "Unable to complete your booking."}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <button onClick={() => window.location.reload()} className="wc-btn-primary text-sm">Try again</button>
              <button onClick={() => nav("/")} className="wc-btn-outline text-sm">Back to home</button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="wc-card p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8" /></div>
          <h1 className="text-3xl font-display font-extrabold text-[#0A2540]">Payment received</h1>
          <p className="text-slate-600 mt-2">A confirmation email is on its way. See you on {summary.courseDate ? new Date(summary.courseDate).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" }) : ""}.</p>
          <div className="mt-8 p-5 bg-slate-50 rounded-lg text-left border border-slate-200 max-w-md mx-auto">
            <div className="text-xs uppercase font-semibold text-slate-500 tracking-wide mb-2">Booking summary</div>
            <div className="text-sm text-slate-700 space-y-1">
              <div className="flex justify-between"><span>Email:</span><span className="font-semibold">{summary.studentEmailId || "—"}</span></div>
              <div className="flex justify-between"><span>Course:</span><span className="font-semibold">{summary.courseName || "—"} ({summary.courseCode || "—"})</span></div>
              <div className="flex justify-between"><span>Date:</span><span className="font-semibold">{summary.courseDate ? new Date(summary.courseDate).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }) : "—"}</span></div>
              <div className="flex justify-between"><span>Location:</span><span className="font-semibold">{summary.location || "—"}</span></div>
              <div className="flex justify-between"><span>Trainer:</span><span className="font-semibold">{summary.trainerName || summary.TrainerName || trainerName || "TBA"}</span></div>
              <div className="flex justify-between"><span>Amount paid:</span><span className="font-semibold text-emerald-600">${summary.amountPaid ? Number(summary.amountPaid).toFixed(2) : "0.00"}</span></div>
              <div className="flex justify-between"><span>Booking:</span><span className="font-semibold">{summary.bookingNumber || "—"}</span></div>
              <div className="flex justify-between"><span>Receipt:</span><span className="font-semibold">{summary.receiptNumber || "—"}</span></div>
            </div>
          </div>
          <div className="mt-6 flex gap-3 justify-center">
            <Link to="/student/dashboard" className="wc-btn-primary text-sm" data-testid="go-dashboard">Go to my dashboard</Link>
            <button onClick={() => nav("/")} className="wc-btn-outline text-sm">Back to home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <Link to="/calendar" className="text-sm text-slate-500 hover:text-[#0A2540] inline-flex items-center gap-1 mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Back to calendar</Link>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        {/* Payment form */}
        <div className="wc-card p-8">
          <div className="flex items-center gap-2 mb-6"><Lock className="w-4 h-4 text-emerald-600" /><span className="text-xs uppercase font-semibold text-emerald-700 tracking-wide">Secure checkout</span></div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">Complete your booking</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Payments are processed securely by Pay Advantage.</p>

          <form onSubmit={pay} className="space-y-4" data-testid="checkout-form">
            <div className="pt-4 border-t border-slate-200">
              <div className="text-sm font-semibold text-[#0A2540] mb-3">Student details</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><label className="wc-label">First name</label><input required className="wc-input" value={student.firstName} onChange={(e) => setStudent({ ...student, firstName: e.target.value })} placeholder="First name" /></div>
                <div><label className="wc-label">Last name</label><input required className="wc-input" value={student.lastName} onChange={(e) => setStudent({ ...student, lastName: e.target.value })} placeholder="Last name" /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <div><label className="wc-label">Email</label><input required type="email" className="wc-input" value={student.email} onChange={(e) => setStudent({ ...student, email: e.target.value })} placeholder="you@example.com" data-testid="checkout-email" /></div>
                <div><label className="wc-label">Mobile number</label><input required className="wc-input" value={student.mobile} onChange={(e) => setStudent({ ...student, mobile: e.target.value })} placeholder="0400 000 000" /></div>
              </div>
              <div className="mt-3"><label className="wc-label">Date of birth</label><input required type="date" className="wc-input" value={student.dob} onChange={(e) => setStudent({ ...student, dob: e.target.value })} /></div>
            </div>

            {paymentError && <div role="alert" className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{paymentError}</div>}

            <div className="pt-4 border-t border-slate-200">
              <div className="text-sm font-semibold text-[#0A2540] mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Secure payment</div>
              <iframe
                ref={iframeRef}
                id="payment_iframe"
                title="Pay Advantage secure payment"
                allow="payment"
                className="w-full border-0 min-h-[220px]"
              />
            </div>

            <button type="submit" disabled={processing || iframeLoading || !paymentScriptReady} className="wc-btn-primary w-full justify-center mt-4" data-testid="checkout-pay">
              {processing ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Processing…</> : <><Lock className="w-4 h-4" /> Pay ${total.toFixed(2)} AUD</>}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-200 flex items-center gap-4 text-xs text-slate-500 justify-center flex-wrap">
            <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />PCI DSS compliant</span>
            <span className="inline-flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-emerald-600" />TLS 1.3 encrypted</span>
            <span className="font-bold text-slate-400">Pay Advantage</span>
          </div>
        </div>

        {/* Order summary */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="wc-card p-6">
            <div className="text-xs uppercase font-semibold text-slate-500 tracking-wide mb-3">Order summary</div>
            <div className="font-display font-extrabold text-lg text-[#0A2540]">{cls?.CourseName || cls?.courseName} · {cls?.CourseCode || cls?.courseCode}</div>
            <div className="mt-3 space-y-2 text-sm text-slate-600 pb-4 border-b border-slate-200">
              <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-orange-500" />{scheduleDate ? scheduleDate.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }) : cls?.date} · {scheduleTime || cls?.time}</div>
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-orange-500" />{locationText || cls?.location}</div>
              <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-orange-500" />Trainer: {trainerName}</div>
            </div>
            <div className="py-4 text-sm border-b border-slate-200"><div className="flex justify-between"><span className="text-slate-600">Course fee</span><span>${subtotal.toFixed(2)}</span></div></div>
            <div className="flex justify-between items-baseline pt-4">
              <span className="font-semibold text-[#0A2540]">Total</span>
              <span className="text-3xl font-display font-extrabold text-[#0A2540]">${total.toFixed(2)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
